/* eslint-disable @typescript-eslint/restrict-template-expressions */
import * as React from 'react';
import { styled } from 'styled-components';

import { createResponsiveSpacingStyle } from '../../../utils';
import type { ResponsiveSpacing } from '../../../utils/types';
import { useMediaMax } from '../../Media';
import { Flex } from '../Flex';

type ResponsiveSpacerProps = {
  gutterWidth?: number | number[];
  gutterHeight?: number | number[];
  marginTop?: ResponsiveSpacing;
  marginBottom?: ResponsiveSpacing;
  marginLeft?: ResponsiveSpacing;
  marginRight?: ResponsiveSpacing;
  breakpoint?: number;
  flexGrow?: number;
  children: React.ReactNode;
};

const MainContainer = styled(Flex)<ResponsiveSpacerProps>`
  ${props =>
    props.gutterWidth && `${createResponsiveSpacingStyle('margin-left', getHalfNegativeNumber(props.gutterWidth))}`};
  ${props =>
    props.gutterWidth && `${createResponsiveSpacingStyle('margin-right', getHalfNegativeNumber(props.gutterWidth))}`};
  ${props => props.marginTop && `${createResponsiveSpacingStyle('margin-top', props.marginTop)}`};
  ${props => props.marginBottom && `${createResponsiveSpacingStyle('margin-bottom', props.marginBottom)}`};
  ${props => props.marginLeft && `${createResponsiveSpacingStyle('margin-left', props.marginLeft)}`};
  ${props => props.marginRight && `${createResponsiveSpacingStyle('margin-right', props.marginRight)}`};
`;

const ChildContainer = styled.div<ResponsiveSpacerProps>`
  box-sizing: border-box;
  display: inline-flex;
  ${props => props.gutterHeight && `${createResponsiveSpacingStyle('margin-top', getHalfNumber(props.gutterHeight))}`};
  ${props =>
    props.gutterHeight && `${createResponsiveSpacingStyle('margin-bottom', getHalfNumber(props.gutterHeight))}`};
  ${props => props.gutterWidth && `${createResponsiveSpacingStyle('padding-left', getHalfNumber(props.gutterWidth))}`};
  ${props => props.gutterWidth && `${createResponsiveSpacingStyle('padding-right', getHalfNumber(props.gutterWidth))}`};
  flex-grow: ${props => props.flexGrow};

  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

const half = (value: number) => value / 2;
const negate = (value: number) => -value;

const getTransformedNumber = (value: number | number[], transformationFn: any) => {
  if (typeof value === 'number') {
    return transformationFn(value);
  } else if (Array.isArray(value)) {
    return value.map(number => transformationFn(number));
  }
  return 0;
};

const getHalfNumber = (value: number | number[]) => getTransformedNumber(value, half);
const getNegativeNumber = (value: number | number[]) => getTransformedNumber(value, negate);
const getHalfNegativeNumber = (value: number | number[]) => getNegativeNumber(getHalfNumber(value));

export const ResponsiveSpacer = ({
  children,
  gutterWidth,
  gutterHeight,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  flexGrow,
  breakpoint,
  ...rest
}: ResponsiveSpacerProps & any) => {
  let shouldBreak = false;
  if (breakpoint) {
    shouldBreak = useMediaMax(breakpoint);
  }

  const filteredChildren = React.Children.toArray(children);
  const styledChildren = React.Children.map(filteredChildren, child => {
    if (React.isValidElement(child)) {
      return (
        <ChildContainer flexGrow={flexGrow} gutterWidth={gutterWidth} gutterHeight={shouldBreak ? gutterHeight : 0}>
          {child}
        </ChildContainer>
      );
    }
    return child;
  });

  return (
    <MainContainer
      gutterWidth={gutterWidth}
      gutterHeight={shouldBreak ? gutterHeight : 0}
      marginTop={marginTop}
      marginBottom={marginBottom}
      marginLeft={marginLeft}
      marginRight={marginRight}
      flexDirection={shouldBreak ? 'column' : 'row'}
      alignItems={shouldBreak ? 'stretch' : 'center'}
      {...rest}
    >
      {styledChildren}
    </MainContainer>
  );
};
